<template>
  <div class="ma-2" id="vpn-profile__action-container">
    <vpn-certificates-action />
    <v-btn
      color="primary"
      id="vpn-profile__hide-show-profile"
      class="ml-2"
      @click="toggleVpnProfile"
    >
      {{
        profilesVisible
          ? $t("modules.dataPath.vpn.hideVpnProfiles")
          : $t("modules.dataPath.vpn.showVpnProfiles")
      }}
    </v-btn>
    <div class="mt-4" v-if="profilesVisible">
      <v-btn
        color="primary"
        id="vpn-profile__open-dialog"
        @click="openVpnProfileDialog"
        :disabled="!isPermitted(PermissionEnum.OPENVPN_PROFILE.ADD)"
      >
        {{ $t("modules.dataPath.vpn.create") }}
      </v-btn>
    </div>
    <v-dialog
      v-model="vpnProfileDialog"
      persistent
      max-width="600"
      id="vpn-profile__dialog"
      v-if="
        isPermitted(PermissionEnum.OPENVPN_PROFILE.ADD) ||
          isPermitted(PermissionEnum.OPENVPN_PROFILE.UPDATE)
      "
    >
      <v-card id="vpn-profile__form-card">
        <v-form
          ref="createVpnProfileForm"
          id="vpn-profile__form"
          @submit.prevent="saveVpnProfile()"
        >
          <v-card-title id="vpn-profile_form-title">
            {{ formTitle }}
          </v-card-title>
          <v-card-text>
            <v-container>
              <div id="vpn-profile__form-fields">
                <v-row>
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="name"
                      :label="$t('modules.dataPath.vpn.name')"
                      id="vpn-profile__form-profile-name"
                      :rules="vpnProfileNameRules"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-checkbox
                      v-model="isEnabled"
                      class="float-right"
                      color="primary"
                      :label="$t('modules.dataPath.enabled')"
                      id="vpn-profile__form-enabled"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row class="d-flex align-center justify-center">
                  <v-col cols="12" md="9">
                    <v-select
                      :label="$t('modules.dataPath.vpn.profileMode')"
                      id="vpn-profile__form-profile-mode"
                      :items="profile_modes"
                      v-model="is_private"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon fav small dark color="primary" v-on="on"
                          ><v-icon dark>mdi-information</v-icon></v-btn
                        >
                      </template>
                      <span>{{
                        $t("modules.dataPath.vpn.automaticTooltipText")
                      }}</span>
                      <br />
                      <span>{{
                        $t("modules.dataPath.vpn.privateTooltipText")
                      }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>

                <v-container class="elevation-2 my-6">
                  <div class="mb-4">
                    <span class="subheading font-weight-bold">
                      {{ $t("modules.dataPath.vpn.vpnConfiguration") }}
                    </span>
                    <span class="grey--text ml-1">
                      (
                      {{ $t("modules.dataPath.vpn.vpnConfigurationComment") }} )
                    </span>
                  </div>
                  <v-flex>
                    <v-select
                      :label="$t('modules.dataPath.vpn.encryptionType')"
                      id="vpn-profile__form-profile-encryption-type"
                      :items="encryption_types"
                      v-model="encryption_type"
                      :rules="[
                        v =>
                          !!v ||
                          $t('validation.required', [
                            $t('modules.dataPath.vpn.encryptionType')
                          ])
                      ]"
                    ></v-select>
                    <v-select
                      :label="$t('modules.dataPath.vpn.compressionType')"
                      id="vpn-profile__form-profile-compression-type"
                      :items="compression_types"
                      v-model="compression_type"
                      :rules="[
                        v =>
                          !!v ||
                          $t('validation.required', [
                            $t('modules.dataPath.vpn.compressionType')
                          ])
                      ]"
                    ></v-select>
                    <v-select
                      :label="$t('modules.dataPath.vpn.layerType')"
                      id="vpn-profile__form-profile-layer-type"
                      :items="layer_types"
                      v-model="layer_type"
                      :rules="[
                        v =>
                          !!v ||
                          $t('validation.required', [
                            $t('modules.dataPath.vpn.layerType')
                          ])
                      ]"
                    >
                    </v-select>
                    <div v-if="moreOptions">
                      <v-text-field
                        v-model.number="fragment"
                        type="number"
                        :label="$t('modules.dataPath.vpn.fragment')"
                        id="vpn-profile__form-profile-fragment"
                        :rules="fragmentMssfixRules"
                      ></v-text-field>
                      <v-text-field
                        v-model.number="mssfix"
                        type="number"
                        :label="$t('modules.dataPath.vpn.mssfix')"
                        id="vpn-profile__form-profile-mssfix"
                        :rules="fragmentMssfixRules"
                      ></v-text-field>
                      <v-select
                        :label="$t('modules.dataPath.vpn.auth')"
                        id="vpn-profile__form-profile-auth"
                        :items="auth_types"
                        v-model="auth"
                        clearable
                      >
                      </v-select>
                      <v-select
                        :label="$t('modules.dataPath.vpn.keyDirection')"
                        id="vpn-profile__form-profile-key-direction"
                        :items="key_directions"
                        v-model="key_direction"
                        clearable
                      >
                      </v-select>
                      <v-select
                        :label="$t('modules.dataPath.vpn.remoteCertTls')"
                        id="vpn-profile__form-profile-remote-cert-tls"
                        :items="certificationTypes"
                        v-model="remote_cert_tls"
                        clearable
                      >
                      </v-select>
                      <v-text-field
                        v-model="tls_auth"
                        :label="$t('modules.dataPath.vpn.tlsAuth')"
                        id="vpn-profile__form-profile-tls-auth"
                      ></v-text-field>
                      <v-select
                        :label="$t('modules.dataPath.vpn.authUser')"
                        id="vpn-profile__form-profile-auth-user"
                        :items="auth_user_pass"
                        v-model="type"
                        clearable
                      >
                      </v-select>
                      <div v-if="type">
                        <v-text-field
                          v-if="type === 1"
                          v-model="username"
                          :label="$t('modules.dataPath.vpn.username')"
                          id="vpn-profile__form-profile-username"
                        ></v-text-field>
                        <v-text-field
                          v-else
                          :label="$t('modules.dataPath.vpn.dynamicPerDevice')"
                          id="vpn-profile__form-profile-username"
                          disabled
                        ></v-text-field>
                        <v-text-field
                          v-model="password"
                          type="password"
                          :label="$t('modules.dataPath.vpn.password')"
                          id="vpn-profile__form-profile-password"
                          :rules="[
                            v =>
                              (v && v.length <= 30) ||
                              $t('validation.lessCharacters', [30])
                          ]"
                        ></v-text-field>
                      </div>
                      <div class="mt-4">
                        <span class="subheading font-weight-bold">
                          {{ $t("modules.dataPath.vpn.custom") }}
                        </span>
                        <div
                          v-for="(item, index) in custom_fields"
                          :key="index"
                        >
                          <v-row class="d-flex align-center justify-center">
                            <v-col cols="12" md="4">
                              <v-text-field
                                v-model="item.key_name"
                                :label="$t('modules.dataPath.vpn.key')"
                                :rules="customFieldsRules"
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-text-field
                                v-model="item.key_value"
                                :label="$t('modules.dataPath.vpn.value')"
                                :rules="customKeyValueRules"
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                  <v-btn
                                    icon
                                    fav
                                    small
                                    dark
                                    color="primary"
                                    v-on="on"
                                    ><v-icon dark
                                      >mdi-information</v-icon
                                    ></v-btn
                                  >
                                </template>
                                <span>{{
                                  $t("modules.dataPath.vpn.notAllowedKeys")
                                }}</span>
                                <ul>
                                  <li
                                    v-for="(key, i) in not_allowed_keys"
                                    :key="i"
                                  >
                                    {{ key.text }}
                                  </li>
                                </ul>
                              </v-tooltip>
                              <v-btn
                                icon
                                color="red"
                                v-if="custom_fields.length > 1"
                                @click="deleteRow(index)"
                              >
                                <v-icon>mdi-minus-circle</v-icon>
                              </v-btn>
                            </v-col>
                          </v-row>
                          <v-btn
                            plain
                            v-if="index === custom_fields.length - 1"
                            @click.prevent="addFormRow()"
                          >
                            + ADD
                          </v-btn>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex align-center justify-end">
                      <v-btn
                        color="secondary"
                        elevation="1"
                        id="vpn-profile__form-more-options"
                        @click="toggleMoreOptionsDialog"
                      >
                        {{
                          moreOptions
                            ? $t("modules.dataPath.vpn.lessOptions")
                            : $t("modules.dataPath.vpn.moreOptions")
                        }}
                      </v-btn>
                    </div>
                  </v-flex>
                </v-container>

                <v-container class="elevation-2 mt-6">
                  <div class="subheading font-weight-bold mb-4">
                    {{ $t("modules.dataPath.vpn.vpnServerConfiguration") }}
                  </div>
                  <v-text-field
                    id="vpn-profile__form-profile-server-ip-address"
                    v-model="server_ip_address"
                    :label="$t('modules.dataPath.vpn.serverIpAddress')"
                    class="ml-1"
                    :rules="serverIpAddressRules"
                  ></v-text-field>
                  <v-text-field
                    id="vpn-profile__form-profile-port"
                    type="number"
                    step="1"
                    v-model.number="port"
                    :label="$t('modules.dataPath.vpn.serverIpPort')"
                    class="mr-1"
                    :rules="[
                      v =>
                        !!v ||
                        $t('validation.required', [
                          $t('modules.dataPath.vpn.serverIpPort')
                        ]),
                      value =>
                        (value >= MIN_PORT && value <= MAX_PORT) ||
                        $t('validation.invalidPortRange', [MIN_PORT, MAX_PORT])
                    ]"
                  ></v-text-field>
                  <div class="text-right">
                    <v-btn
                      color="secondary"
                      elevation="1"
                      id="vpn-profile__form-advanced"
                      @click="toggleAdvancedDialog"
                    >
                      {{ $t("modules.dataPath.vpn.advanced") }}
                    </v-btn>
                  </div>
                </v-container>
              </div>
            </v-container>

            <v-dialog v-model="advancedDialog" max-width="550" persistent>
              <v-card>
                <v-form
                  ref="advancedForm"
                  id="vpn-profile__advanced-form"
                  @submit.prevent="saveAdvancedDialog()"
                >
                  <v-card-text class="pt-4">
                    <v-container class="my-6">
                      <v-flex>
                        <div class="mb-4">
                          <span class="subheading font-weight-bold black--text">
                            {{ $t("modules.dataPath.vpn.vpnServerAddresses") }}
                          </span>
                          <span class="grey--text ml-1">
                            (
                            {{ $t("modules.dataPath.vpn.serverConfigComment") }}
                            )
                          </span>
                        </div>
                        <v-text-field
                          v-model="ip_address_alt"
                          :label="$t('modules.dataPath.vpn.ipAddress')"
                          id="vpn-profile__form-profile-ip-address"
                          :rules="ipAddressRules"
                        ></v-text-field>
                        <v-text-field
                          v-model="subnet_mask_alt"
                          :label="$t('modules.dataPath.vpn.subnetMask')"
                          id="vpn-profile__form-profile-subnet-mask"
                          :rules="subnetRules"
                        ></v-text-field>
                      </v-flex>
                    </v-container>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="secondary"
                        elevation="1"
                        id="vpn-profile__advanced-form-cancel"
                        @click="closeAdvancedDialog"
                      >
                        {{ $t("actions.cancel") }}
                      </v-btn>
                      <v-btn
                        color="secondary"
                        elevation="1"
                        id="vpn-profile__advanced-form-save"
                        @click="saveAdvancedDialog"
                      >
                        {{ $t("actions.save") }}
                      </v-btn>
                    </v-card-actions>
                  </v-card-text>
                </v-form>
              </v-card>
            </v-dialog>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="secondary"
              elevation="1"
              id="vpn-profile__form-cancel"
              @click="closeVpnProfileDialog"
            >
              {{ $t("actions.cancel") }}
            </v-btn>
            <v-btn
              color="secondary"
              type="submit"
              id="vpn-profile__form-add"
              :loading="is_loading"
              :disabled="is_loading_global"
            >
              {{ editedIndex === -1 ? $t("actions.add") : $t("actions.save") }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { handleError } from "@/utils/error";
import util from "@/modules/data-path/util";
import { mapMultiRowFields } from "vuex-map-fields";
import { permissionMixin } from "@/mixins/permissions";
import VpnCertificatesAction from "../vpn-certificates/vpn-certificates-action.vue";

export default {
  mixins: [permissionMixin],
  components: {
    VpnCertificatesAction
  },
  data() {
    return {
      MIN_PORT: 1000,
      MAX_PORT: 10000,
      ip_address_alt: "",
      subnet_mask_alt: "",
      certificationTypes: [
        {
          text: "Client",
          value: 1
        },
        {
          text: "Server",
          value: 2
        }
      ],
      util,
      profile_modes: [
        {
          text: this.$t("modules.dataPath.vpn.automatic"),
          value: false
        },
        {
          text: this.$t("modules.dataPath.vpn.privateCertificateService"),
          value: true
        }
      ],
      encryption_types: [
        {
          text: this.$t("modules.dataPath.vpn.none"),
          value: 1
        },
        {
          text: this.$t("modules.dataPath.vpn.blowfish"),
          value: 2
        },
        {
          text: this.$t("modules.dataPath.vpn.aes256"),
          value: 3
        }
      ],
      compression_types: [
        {
          text: this.$t("modules.dataPath.vpn.none"),
          value: 1
        },
        {
          text: this.$t("modules.dataPath.vpn.lzo"),
          value: 2
        },
        {
          text: this.$t("modules.dataPath.vpn.lz4"),
          value: 3
        }
      ],
      layer_types: [
        {
          text: this.$t("modules.dataPath.vpn.layer2"),
          value: 1
        },
        {
          text: this.$t("modules.dataPath.vpn.layer3"),
          value: 2
        }
      ],
      auth_types: [
        {
          text: this.$t("modules.dataPath.vpn.sha1"),
          value: 1
        },
        {
          text: this.$t("modules.dataPath.vpn.sha256"),
          value: 2
        }
      ],
      key_directions: [
        {
          text: this.$t("modules.dataPath.vpn.hmacSend"),
          value: 0
        },
        {
          text: this.$t("modules.dataPath.vpn.cipherEncrypt"),
          value: 1
        },
        {
          text: this.$t("modules.dataPath.vpn.hmacReceive"),
          value: 2
        },
        {
          text: this.$t("modules.dataPath.vpn.cipherDecrypt"),
          value: 3
        }
      ],
      auth_user_pass: [
        {
          text: this.$t("modules.dataPath.vpn.staticUser"),
          value: 1
        },
        {
          text: this.$t("modules.dataPath.vpn.dynamicUser"),
          value: 2
        }
      ],
      not_allowed_keys: [
        {
          text: this.$t("modules.dataPath.vpn.up")
        },
        {
          text: this.$t("modules.dataPath.vpn.tlsVerify")
        },
        {
          text: this.$t("modules.dataPath.vpn.ipchange")
        },
        {
          text: this.$t("modules.dataPath.vpn.clientConnect")
        },
        {
          text: this.$t("modules.dataPath.vpn.routeUp")
        },
        {
          text: this.$t("modules.dataPath.vpn.routePreDown")
        },
        {
          text: this.$t("modules.dataPath.vpn.clientDisconnect")
        },
        {
          text: this.$t("modules.dataPath.vpn.down")
        },
        {
          text: this.$t("modules.dataPath.vpn.learnAddress")
        },
        {
          text: this.$t("modules.dataPath.vpn.authUserPassVerify")
        },
        {
          text: this.$t("modules.dataPath.vpn.authUserPass")
        }
      ],
      vpnProfileNameRules: [
        v =>
          !!v ||
          this.$t("validation.required", [
            this.$t("modules.dataPath.vpn.name")
          ]),
        value =>
          /^[a-zA-Z0-9-_]+$/.test(value) ||
          this.$t("modules.dataPath.vpn.nameValidMessage")
      ],
      serverIpAddressRules: [
        v =>
          !!v ||
          this.$t("validation.required", [
            this.$t("modules.dataPath.vpn.serverIpAddress")
          ])
      ],
      ipAddressRules: [
        value =>
          !value ||
          util.IP_REGEX.test(value) ||
          this.$t("validation.invalid", [
            this.$t("modules.dataPath.vpn.ipAddress")
          ]),
        v => {
          if (!v && this.validateIpAddressValues()) {
            return this.$t("validation.required", [
              this.$t("modules.dataPath.vpn.ipAddress")
            ]);
          }
          return true;
        }
      ],
      subnetRules: [
        value =>
          !value ||
          util.SUBNET_REGEX.test(value) ||
          this.$t("validation.invalid", [
            this.$t("modules.dataPath.vpn.subnetMask")
          ]),
        v => {
          if (!v && this.validateSubnetMaskValues()) {
            return this.$t("validation.required", [
              this.$t("modules.dataPath.vpn.subnetMask")
            ]);
          }
          return true;
        }
      ],
      fragmentMssfixRules: [
        v => {
          if ((!!v && v < 100) || v === 0) {
            return this.$t("validation.moreEqual", [100]);
          }
          return true;
        }
      ],
      customFieldsRules: [
        v => {
          if (!!v && this.validateNotAllowedKeys(v)) {
            return this.$t("error.notAllowedCustomKeys");
          } else if (!!v && this.validateDuplicateKeys(v)) {
            return this.$t("error.duplicateCustomKey");
          } else if (!v && this.validateCustomKeys()) {
            return this.$t("validation.required", [
              this.$t("modules.dataPath.vpn.key")
            ]);
          }
          return true;
        }
      ],
      customKeyValueRules: [
        v => {
          if (!v && this.validateCustomValues()) {
            return this.$t("validation.required", [
              this.$t("modules.dataPath.vpn.value")
            ]);
          }
          return true;
        }
      ]
    };
  },

  computed: {
    ...mapMultiRowFields("dataPath", {
      custom_fields: "vpnProfile.editedItem.custom_fields"
    }),
    is_loading() {
      return this.$store.state.loader.is_loading;
    },
    is_loading_global() {
      return this.$store.state.loader.is_loading_global;
    },
    profilesVisible: {
      get() {
        return this.$store.state.dataPath.vpnProfile.profilesVisible;
      },
      set(val) {
        this.$store.dispatch("dataPath/toggleVpnProfilesVisibility", val);
      }
    },
    vpnProfileDialog: {
      get() {
        return this.$store.state.dataPath.vpnProfile.vpnProfileDialog;
      },
      set(val) {
        this.$store.dispatch("dataPath/toggleVpnProfileDialog", val);
      }
    },
    name: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.name;
      },
      set(val) {
        this.$store.commit("dataPath/updateVpnProfileName", val);
      }
    },
    isEnabled: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.isEnabled;
      },
      set(val) {
        this.$store.commit("dataPath/updateVpnIsEnabled", val);
      }
    },
    is_private: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.is_private;
      },
      set(val) {
        this.$store.commit("dataPath/updateIsPrivate", val);
      }
    },
    encryption_type: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.encryption_type;
      },
      set(val) {
        this.$store.commit("dataPath/updateEncryptionType", val);
      }
    },
    compression_type: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem
          .compression_type;
      },
      set(val) {
        this.$store.commit("dataPath/updateCompressionType", val);
      }
    },
    layer_type: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.layer_type;
      },
      set(val) {
        this.$store.commit("dataPath/updateLayerType", val);
      }
    },
    ip_address: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.ip_address;
      },
      set(val) {
        this.$store.commit("dataPath/updateIpAddress", val);
      }
    },
    port: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.port;
      },
      set(val) {
        this.$store.commit("dataPath/updatePort", val);
      }
    },
    subnet_mask: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.subnet_mask;
      },
      set(val) {
        this.$store.commit("dataPath/updateSubnetMask", val);
      }
    },
    server_ip_address: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem
          .server_ip_address;
      },
      set(val) {
        this.$store.commit("dataPath/updateServerIpAddress", val);
      }
    },
    fragment: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.fragment;
      },
      set(val) {
        this.$store.commit("dataPath/updateFragment", val);
      }
    },
    mssfix: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.mssfix;
      },
      set(val) {
        this.$store.commit("dataPath/updateMssfix", val);
      }
    },
    auth: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.auth;
      },
      set(val) {
        this.$store.commit("dataPath/updateAuth", val);
      }
    },
    key_direction: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.key_direction;
      },
      set(val) {
        this.$store.commit("dataPath/updateKeyDirection", val);
      }
    },
    remote_cert_tls: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.remote_cert_tls;
      },
      set(val) {
        this.$store.commit("dataPath/updateRemoteCertTls", val);
      }
    },
    tls_auth: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.tls_auth;
      },
      set(val) {
        this.$store.commit("dataPath/updateTlsAuth", val);
      }
    },
    type: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.type;
      },
      set(val) {
        this.$store.commit("dataPath/updateType", val);
      }
    },
    username: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.username;
      },
      set(val) {
        this.$store.commit("dataPath/updateUsername", val);
      }
    },
    password: {
      get() {
        return this.$store.state.dataPath.vpnProfile.editedItem.password;
      },
      set(val) {
        this.$store.commit("dataPath/updatePassword", val);
      }
    },
    custom_fields_alt() {
      return this.$store.state.dataPath.vpnProfile.editedItem.custom_fields;
    },
    advancedDialog: {
      get() {
        return this.$store.state.dataPath.vpnProfile.advancedDialog;
      },
      set(val) {
        this.$store.dispatch("dataPath/toggleAdvancedDialog", val);
      }
    },
    moreOptions: {
      get() {
        return this.$store.state.dataPath.vpnProfile.moreOptions;
      },
      set(val) {
        this.$store.dispatch("dataPath/toggleMoreOptionsDialog", val);
      }
    },
    formTitle() {
      return this.$store.state.dataPath.vpnProfile.formTitle;
    },
    editedItem() {
      return this.$store.state.dataPath.vpnProfile.editedItem;
    },
    editedIndex() {
      return this.$store.state.dataPath.vpnProfile.editedIndex;
    },
    vpnProfiles() {
      return this.$store.state.dataPath.vpnProfile.vpnProfiles;
    }
  },
  methods: {
    toggleVpnProfile() {
      this.profilesVisible = !this.profilesVisible;
    },
    openVpnProfileDialog() {
      this.$store.commit("dataPath/updateVpnProfileIndex", -1);
      this.$store.commit("dataPath/updateVpnProfileFormTitle", 1);
      this.$store.dispatch("dataPath/toggleVpnProfileDialog", true).then(() => {
        this.$refs.createVpnProfileForm.resetValidation();
      });
    },
    closeVpnProfileDialog() {
      this.$store
        .dispatch("dataPath/toggleVpnProfileDialog", false)
        .then(() => {
          this.$refs.createVpnProfileForm.resetValidation();
          this.$store.dispatch("dataPath/clearVpnProfileForm");
          this.$store.commit("dataPath/updateVpnProfileIndex", -1);
        });
    },
    saveVpnProfile() {
      if (this.$refs.createVpnProfileForm.validate()) {
        this.$store.dispatch("loader/startLoading");
        if (this.editedIndex > -1) {
          let data = {
            id: this.vpnProfiles[this.editedIndex].id,
            name: this.name,
            is_enabled: this.isEnabled,
            is_private: this.is_private,
            encryption_type: this.encryption_type,
            compression_type: this.compression_type,
            layer_type: this.layer_type,
            ip_address: this.ip_address,
            subnet_mask: this.subnet_mask,
            server_address: this.server_ip_address,
            port: this.port,
            fragment: this.fragment,
            mssfix: this.mssfix,
            auth: this.auth,
            key_direction: this.key_direction,
            remote_cert_tls: this.remote_cert_tls,
            tls_auth: this.tls_auth,
            type: this.type,
            username: this.username,
            password: this.password,
            custom_fields: this.custom_fields_alt
          };
          this.$store
            .dispatch("dataPath/updateVpnProfile", data)
            .catch(error => {
              handleError(error);
            });
        } else {
          let data = {
            name: this.name,
            is_enabled: this.isEnabled,
            is_private: this.is_private,
            encryption_type: this.encryption_type,
            compression_type: this.compression_type,
            layer_type: this.layer_type,
            ip_address: this.ip_address,
            subnet_mask: this.subnet_mask,
            server_address: this.server_ip_address,
            port: this.port,
            fragment: this.fragment,
            mssfix: this.mssfix,
            auth: this.auth,
            key_direction: this.key_direction,
            remote_cert_tls: this.remote_cert_tls,
            tls_auth: this.tls_auth,
            type: this.type,
            username: this.username,
            password: this.password,
            custom_fields: this.custom_fields_alt
          };
          this.$store
            .dispatch("dataPath/createVpnProfile", data)
            .catch(error => {
              handleError(error);
            });
        }
        this.$refs.createVpnProfileForm.resetValidation();
      }
    },
    toggleAdvancedDialog() {
      this.ip_address_alt = this.ip_address;
      this.subnet_mask_alt = this.subnet_mask;
      this.advancedDialog = !this.advancedDialog;
    },
    saveAdvancedDialog() {
      if (this.$refs.advancedForm.validate()) {
        this.ip_address = this.ip_address_alt;
        this.subnet_mask = this.subnet_mask_alt;
        this.advancedDialog = !this.advancedDialog;
      }
    },
    closeAdvancedDialog() {
      if (this.editedIndex === -1) {
        this.$store.commit("dataPath/clearVpnAdvancedForm");
      }
      this.ip_address_alt = this.ip_address;
      this.subnet_mask_alt = this.subnet_mask;
      this.advancedDialog = !this.advancedDialog;
      this.$refs.advancedForm.resetValidation();
    },
    toggleMoreOptionsDialog() {
      this.moreOptions = !this.moreOptions;
    },
    addFormRow() {
      this.$store.commit("dataPath/addCustomFieldRow");
    },
    deleteRow(index) {
      this.$store.commit("dataPath/removeCustomFieldRow", index);
    },
    validateNotAllowedKeys(value) {
      let result = this.not_allowed_keys.some(function(key) {
        return value === key.text;
      });
      return result;
    },
    validateDuplicateKeys(value) {
      if (this.custom_fields_alt.length > 1) {
        if (
          this.custom_fields_alt.reduce((total, custom_field) => {
            return custom_field.key_name === value ? ++total : total;
          }, 0) > 1
        ) {
          return true;
        }
      }
      return false;
    },
    validateCustomKeys() {
      if (
        this.custom_fields_alt.reduce((total, custom_field) => {
          return custom_field.key_value && !custom_field.key_name
            ? ++total
            : total;
        }, 0)
      ) {
        return true;
      }
      return false;
    },
    validateCustomValues() {
      if (
        this.custom_fields_alt.reduce((total, custom_field) => {
          return custom_field.key_name && !custom_field.key_value
            ? ++total
            : total;
        }, 0)
      ) {
        return true;
      }
      return false;
    },
    validateIpAddressValues() {
      return this.subnet_mask_alt && !this.ip_address_alt ? true : false;
    },
    validateSubnetMaskValues() {
      return this.ip_address_alt && !this.subnet_mask_alt ? true : false;
    }
  },
  watch: {
    mode(val) {
      if (val === this.util.SERVER && this.config_source === 2) {
        this.config_source = 1;
      }
    },
    ip_address_alt(val) {
      if (!val && !this.subnet_mask_alt) {
        if (this.$refs.advancedForm) {
          this.$refs.advancedForm.resetValidation();
        }
      }
    },
    subnet_mask_alt(val) {
      if (!val && !this.ip_address_alt) {
        if (this.$refs.advancedForm) {
          this.$refs.advancedForm.resetValidation();
        }
      }
    }
  }
};
</script>
